import 'core-js/stable';
import 'regenerator-runtime/runtime';

import flatten from 'flat';
import { createRoot } from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { HashRouter } from 'react-router-dom';
import App from './App';
import './globalStyles/global.scss';
import { getAppQualifier } from './utils/appQualifier';
import { CheckoutState, CheckoutStateProvider, setBasketId } from './utils/checkoutState';
import { DISTRIBUTION_URL, getCdnShopUrl, loadConfig } from './utils/config';
import formatLocale from './utils/formatLocale';
import { initializeSentry } from './utils/initializeSentry';
import loadPolyfills from './utils/loadPolyfills';
import { NotificationsProvider } from './utils/notifications';

if (USE_MOCK_API) {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  require('./mockApi');
}

class CheckoutWebComponent extends HTMLElement {
  isAuthenticated = false;
  isInitialUpdateDone = false;

  async connectedCallback() {
    const shadowRoot = this.attachShadow({ mode: 'open' });

    // Get app qualifier and basket ID from component attributes
    const injectedAppQualifier = window.ayCheckout?.appQualifier ?? window.appQualifier;
    const appQualifier = getAppQualifier({ element: this, injectedAppQualifier });
    const urlParams = new URLSearchParams(window.location.search);
    // Allow qaBasketId through query param for use with basket generator
    const basketId =
      urlParams.get('qaBasketId') ?? this.getAttribute('basket-id') ?? this.getAttribute('basketId') ?? undefined;
    // For when the storefront already has a valid session token
    const sessionToken = this.getAttribute('session') ?? undefined;
    // For tracking
    const correlationId = this.getAttribute('correlation-id') ?? this.getAttribute('correlationId') ?? undefined;
    // For campaign vouchers that should be pre-applied in the checkout
    const voucher = this.getAttribute('voucher') ?? undefined;
    // For campaignKey that should be pre-applied in the checkout
    const campaignKey = this.getAttribute('campaign-key') ?? this.getAttribute('campaignKey') ?? undefined;
    const alwaysTriggerAuthenticateEvent = urlParams.get('coAuthenticate') === 'true';
    if (alwaysTriggerAuthenticateEvent) {
      const url = new URL(window.location.href);
      url.searchParams.delete('coAuthenticate');
      window.history.pushState(null, '', url.toString());
    }

    const loadConfigAndApplyCssVars = async () => {
      const config = await loadConfig(appQualifier, { token: sessionToken, correlationId });
      Object.entries(config.styles).forEach(([key, value]) => this.style.setProperty(`--${key}`, value));
      return config;
    };

    const loadBaseCss = () =>
      new Promise<void>(resolve => {
        const baseCss = document.createElement('link');
        baseCss.rel = 'stylesheet';
        baseCss.href = `${DISTRIBUTION_URL}/ay-checkout-base.min.css`;
        baseCss.addEventListener('load', () => resolve());
        shadowRoot.appendChild(baseCss);
      });

    const loadOverwritesCss = () => {
      // We don't actually wanna wait for this to be loaded so we don't make this a promise
      const overwritesCss = document.createElement('link');
      overwritesCss.rel = 'stylesheet';
      overwritesCss.href = `${getCdnShopUrl(appQualifier)}/overwrites.css`;
      shadowRoot.appendChild(overwritesCss);
    };

    const setupPaymentContainers = () => {
      // Add payment containers outside of shadow dom (Payment SDKs can't find elements inside the shadow dom)
      const adyenPaymentContainer = document.createElement('div');
      adyenPaymentContainer.slot = 'adyen-payment-container';
      adyenPaymentContainer.id = 'adyen-payment-container';
      this.appendChild(adyenPaymentContainer);
      const klarnaPaymentContainer = document.createElement('div');
      klarnaPaymentContainer.slot = 'klarna-payment-container';
      klarnaPaymentContainer.id = 'klarna-payment-container';
      this.appendChild(klarnaPaymentContainer);
      const arvatoPaymentContainer = document.createElement('div');
      arvatoPaymentContainer.slot = 'arvato-payment-container';
      arvatoPaymentContainer.id = 'arvato-payment-container';
      this.appendChild(arvatoPaymentContainer);
    };

    const createPasswordAutofillElement = () => {
      const passwordFieldInput = document.createElement('input');
      passwordFieldInput.type = 'password';
      passwordFieldInput.style.display = 'none';
      this.appendChild(passwordFieldInput);
    };

    const [config] = await Promise.all([
      loadConfigAndApplyCssVars(),
      loadBaseCss(),
      loadOverwritesCss(),
      setupPaymentContainers(),
      createPasswordAutofillElement(),
    ]);

    // Load sentry tracking
    initializeSentry();

    const onUpdate = (checkoutState: CheckoutState) => {
      const isAuthenticated = !!checkoutState.customer && !checkoutState.customer.flags?.isGuest;
      if (isAuthenticated !== this.isAuthenticated) {
        this.isAuthenticated = isAuthenticated;
        if (alwaysTriggerAuthenticateEvent || this.isInitialUpdateDone) {
          this.dispatchEvent(new CustomEvent('authenticate', { detail: checkoutState.customer }));
        }
      }
      this.dispatchEvent(new CustomEvent('update', { detail: checkoutState }));
      if (!this.isInitialUpdateDone) {
        this.isInitialUpdateDone = true;
      }
    };

    const onLoad = () => {
      this.dispatchEvent(new CustomEvent('load'));
    };

    const mountPoint = document.createElement('div');
    mountPoint.setAttribute('id', 'root');
    mountPoint.setAttribute('dir', ['ar_SA', 'he_IL'].includes(config.locale) ? 'rtl' : 'ltr');
    shadowRoot.appendChild(mountPoint);

    createRoot(mountPoint).render(
      <IntlProvider messages={flatten(config.translations)} locale={formatLocale(config.locale)}>
        <NotificationsProvider>
          <CheckoutStateProvider
            basketId={basketId}
            voucher={voucher}
            campaignKey={campaignKey}
            onUpdate={onUpdate}
            onLoad={onLoad}
          >
            <HashRouter>
              <App />
            </HashRouter>
          </CheckoutStateProvider>
        </NotificationsProvider>
      </IntlProvider>
    );
  }

  disconnectedCallback() {
    clearTimeout(window.pollTimer);
  }

  setBasketId(newBasketId: string) {
    // If a qaBasketId is supplied via query params we want to keep using that (needed for use with basket generator)
    if (new URLSearchParams(window.location.search).get('qaBasketId')) {
      return;
    }
    setBasketId(newBasketId);
  }
}

(async () => {
  if (!window.customElements.get('ay-checkout')) {
    await loadPolyfills();
    window.customElements.define('ay-checkout', CheckoutWebComponent);
  }
})();
